import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type {
    DataProviderEnum,
    SensorDataRequestCreateDataTypeEnum,
} from "../../../../../../apiClient/generated";
import { useSensorDataApi } from "../../../../../../hooks";

export const useSensirionData = ({
    siteId,
    startDate,
    endDate,
    dataType = "alerts",
    dataProvider,
}: {
    siteId?: string;
    startDate: Date;
    endDate: Date;
    dataType: SensorDataRequestCreateDataTypeEnum;
    dataProvider?: DataProviderEnum;
}) => {
    const apiClient = useSensorDataApi();

    const { data: sensorDataRequestData, isLoading: requestCreateIsLoading } =
        useQuery({
            queryKey: [
                "sensorDataCreate",
                siteId,
                startDate,
                endDate,
                dataType,
                dataProvider,
            ],
            queryFn: async () => {
                const response = await apiClient.sensorDataCreate({
                    sensorDataRequestCreateRequest: {
                        siteId: siteId,
                        startDate,
                        endDate,
                        dataType,
                        dataProvider,
                    },
                });
                return response;
            },
            enabled: !!siteId,
            refetchOnWindowFocus: false,
            staleTime: 3600,
        });

    const { data: sensorData, isLoading: requestIsLoading } = useQuery({
        queryKey: ["sensorDataRetrieve", sensorDataRequestData?.requestId],
        queryFn: async () => {
            let response: any;
            if (dataType === "alerts") {
                response = await apiClient.sensorDataAlertingRetrieve({
                    id: sensorDataRequestData?.requestId,
                });
            } else {
                response = await apiClient.sensorDataSensorRetrieve({
                    id: sensorDataRequestData?.requestId,
                });
            }
            return response;
        },
        enabled: !!sensorDataRequestData?.requestId,
        staleTime: 3600,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        refetchInterval: (data) => {
            if (
                (data && data.status === "COMPLETED") ||
                (data && data.status === "FAILED")
            ) {
                return false;
            }
            return 1000;
        },
    });

    const isLoading = useMemo(
        () =>
            requestCreateIsLoading ||
            requestIsLoading ||
            (sensorDataRequestData?.requestId &&
                sensorData?.status !== "COMPLETED" &&
                sensorData?.status !== "FAILED"),
        [
            sensorData,
            requestIsLoading,
            requestCreateIsLoading,
            sensorDataRequestData,
        ],
    );

    return {
        isLoading,
        data: sensorData?.responseData || [],
    };
};
